import React, { useState } from "react"
import ManualCourseForm from "../courses/ManualCourseForm"

const CourseSettings = ({ courses, onToggle, onAdd, onUpdate, onDelete }) => {
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingCourse, setEditingCourse] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const handleAdd = async (data) => {
    setIsLoading(true)
    try {
      await onAdd(data)
      setShowAddForm(false)
    } catch (error) {
      console.error("Failed to add course:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleUpdate = async (data) => {
    setIsLoading(true)
    try {
      await onUpdate(editingCourse.id, data)
      setEditingCourse(null)
    } catch (error) {
      console.error("Failed to update course:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async (courseId) => {
    setIsLoading(true)
    try {
      await onDelete(courseId)
      setDeleteConfirm(null)
    } catch (error) {
      console.error("Failed to delete course:", error)
    } finally {
      setIsLoading(false)
    }
  }

  // Separate synced and manual courses
  const syncedCourses = courses?.filter(c => c.synced) || []
  const manualCourses = courses?.filter(c => !c.synced) || []

  return (
    <div className="space-y-6">
      {/* Manual Courses Section */}
      <div className="bg-white rounded-xl border shadow-sm p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Manual Courses</h2>
              <p className="text-sm text-gray-500">
                Create custom courses for assignments not in your LMS
              </p>
            </div>
          </div>

          {!showAddForm && !editingCourse && (
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Course
            </button>
          )}
        </div>

        {/* Add Course Form */}
        {showAddForm && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
            <h3 className="font-medium text-gray-900 mb-3">Add New Course</h3>
            <ManualCourseForm
              onSubmit={handleAdd}
              onCancel={() => setShowAddForm(false)}
              isLoading={isLoading}
            />
          </div>
        )}

        {/* Manual Courses List */}
        {manualCourses.length > 0 ? (
          <div className="space-y-2">
            {manualCourses.map((course) => (
              <div key={course.id}>
                {editingCourse?.id === course.id ? (
                  <div className="p-4 bg-gray-50 rounded-lg border">
                    <h3 className="font-medium text-gray-900 mb-3">Edit Course</h3>
                    <ManualCourseForm
                      course={course}
                      onSubmit={handleUpdate}
                      onCancel={() => setEditingCourse(null)}
                      isLoading={isLoading}
                    />
                  </div>
                ) : deleteConfirm === course.id ? (
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm text-red-800 mb-3">
                      Delete "{course.name}"? This will also delete all {course.assignment_count} assignments in this course.
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                        disabled={isLoading}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleDelete(course.id)}
                        className="flex-1 px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                        disabled={isLoading}
                      >
                        {isLoading ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-3 rounded-lg border bg-white hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => onToggle(course.id, !course.hidden)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          course.hidden ? "bg-gray-200" : "bg-indigo-600"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            course.hidden ? "translate-x-1" : "translate-x-6"
                          }`}
                        />
                      </button>
                      <div>
                        <p className={`font-medium ${course.hidden ? "text-gray-400" : "text-gray-900"}`}>
                          {course.name}
                        </p>
                        {course.course_code && (
                          <p className="text-xs text-gray-500">{course.course_code}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 mr-2">
                        {course.assignment_count} assignments
                      </span>
                      <button
                        onClick={() => setEditingCourse(course)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Edit course"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(course.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete course"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : !showAddForm && (
          <p className="text-sm text-gray-500 text-center py-4">
            No manual courses yet. Click "Add Course" to create one.
          </p>
        )}
      </div>

      {/* Synced Courses Section */}
      {syncedCourses.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Synced Courses</h2>
              <p className="text-sm text-gray-500">
                Courses imported from your LMS - toggle visibility
              </p>
            </div>
          </div>

          <div className="space-y-2">
            {syncedCourses.map((course) => (
              <div
                key={course.id}
                className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  course.hidden ? "bg-gray-50" : "bg-white border"
                }`}
              >
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => onToggle(course.id, !course.hidden)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      course.hidden ? "bg-gray-200" : "bg-indigo-600"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        course.hidden ? "translate-x-1" : "translate-x-6"
                      }`}
                    />
                  </button>
                  <div>
                    <p className={`font-medium ${course.hidden ? "text-gray-400" : "text-gray-900"}`}>
                      {course.name}
                    </p>
                    <p className="text-xs text-gray-500">{course.course_code}</p>
                  </div>
                </div>

                <div className="text-right">
                  <span className="text-sm text-gray-500">
                    {course.assignment_count} assignments
                  </span>
                  {course.pending_count > 0 && !course.hidden && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                      {course.pending_count} pending
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default CourseSettings
